// Types for the self-scheduling UI, backed by ehr-sync-service's /self-scheduling APIs.

export type ConsultationType = 'Virtual' | 'In-person';

export type PayMode = 'InsurancePay' | 'CashPay';

// Response shape of GET /self-scheduling/patients/{patientId}/context - fetched once, up front,
// so payMode (and an existing patient's preselected provider) can be sent explicitly on every
// later preview/availability/booking call instead of each one re-deriving it.
export interface PatientSelfSchedulingContext {
  providerId?: number;
  payMode: PayMode;
  canBookAppointment: boolean;
  // True for an existing patient whose primary provider is no longer available (e.g. left the
  // organization) - online booking isn't allowed; ProviderAvailability shows a call-to-schedule
  // dialog and sends them back to the dashboard. canBookAppointment is false as well.
  primaryProviderUnavailable: boolean;
}

export interface AvailabilitySlot {
  id: string;
  day: string; // display, e.g. 'Thu, Sep 3' (derived from `start`)
  time: string; // display, e.g. '9:40 PM' (derived from `start`)
  start: string; // ISO-8601, office-local (America/Chicago) offset from the backend
  end: string; // ISO-8601
  officeId: number;
  officeName?: string;
  officeCity?: string;
  // More than one entry when this slot's schedule is shared by more than one profile (e.g. a
  // cash-pay and an insurance-pay variant of the same consultation type) - the backend resolves
  // which one is actually booked from the patient's pay-type, so the UI never needs to choose
  // between them; any one entry can be sent back when booking (see selfSchedulingServices).
  profileId: number[];
  examRoom: number;
  duration: number;
}

export interface Provider {
  id: number;
  name: string;
  credential: string;
  description: string;
  consultationTypes: ConsultationType[];
  locations: string[];
  availability: AvailabilitySlot[];
  hasMoreAvailability: boolean;
}

export const CONSULTATION_TYPES: ConsultationType[] = ['Virtual', 'In-person'];
